import type { ReactNode } from "react";
import { DownloadIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";

import { RowActionIconButton } from "../ui/row-action-icon-button";

import type { ThemeAppearance, ThemePalette } from "@cmmd-forge/contracts/theme";
import { DEFAULT_THEME_ID, THEME_PALETTES } from "@cmmd-forge/contracts/theme";

import { ImportThemeControl } from "./ThemeImportControl";
import { openThemeEditor } from "./themeEditorStore";

import { useCustomThemes } from "../../hooks/useCustomThemes";
import { removeCustomTheme, type CustomThemePalette, type ThemeHalves } from "../../themeLibrary";
import { serializeThemeFile } from "../../themeFiles";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { SettingsRow } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";

/**
 * Custom-theme management and the per-appearance mix, for the Appearance
 * panel: importing and exporting theme files, removing installed themes, and
 * pinning a different theme to each appearance.
 *
 * This is a renderer: it owns the file input, the download, and the library
 * mutation calls, and reports outcomes to the panel through callbacks. All
 * theme state (the base preference and the mix) stays in the panel's single
 * `useTheme` instance, so there is exactly one applier in the tree.
 *
 * Row titles come from the settings-search catalog through the
 * `searchableSetting` spread, never from a literal here.
 *
 * Provenance: the import/export/remove flow is modeled on upstream's
 * `ThemeSettings.tsx` + `ThemeImportDialog.tsx` (pingdotgg/t3code, MIT, pin
 * e3e7cc3f) — the same downloadThemeFile helper and import-over-existing
 * behavior, re-expressed as a forge settings section over forge's library.
 * Rules: docs/port-translation-rules.md.
 *
 * @module
 */

/** One selectable theme for a half: the default, a built-in, or an install. */
interface HalfOption {
  readonly id: string;
  readonly name: string;
}

const DEFAULT_HALF_OPTION: HalfOption = { id: "", name: "Forge (default)" };

function halfOptions(customThemes: ReadonlyArray<ThemePalette>): ReadonlyArray<HalfOption> {
  return [
    ...THEME_PALETTES.map((palette) => ({ id: palette.id, name: palette.name })),
    ...customThemes.map((theme) => ({ id: theme.id, name: theme.name })),
  ];
}

/** Download a theme's file form, the way upstream's settings page does. */
function downloadThemeFile(filename: string, contents: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Revoking synchronously can abort the download in some browsers; give the
  // browser time to open the stream first.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * The two appearance pickers of the mix. Empty means the default: the theme
 * preference owns that appearance.
 */
export function ThemeHalvesRow(props: {
  readonly customThemes: ReadonlyArray<ThemePalette>;
  readonly halves: ThemeHalves | null;
  readonly onSetHalf: (appearance: ThemeAppearance, themeId: string | null) => void;
}): ReactNode {
  const options = halfOptions(props.customThemes);
  const nameOf = (id: string): string => options.find((option) => option.id === id)?.name ?? id;

  const renderHalfSelect = (appearance: ThemeAppearance): ReactNode => {
    const selected = props.halves?.[appearance] ?? "";
    return (
      <Select
        onValueChange={(value) => {
          props.onSetHalf(appearance, value === "" ? null : value);
        }}
        value={selected}
      >
        <SelectTrigger
          aria-label={`${appearance === "light" ? "Light" : "Dark"} appearance theme`}
          className="w-full sm:w-44"
        >
          <SelectValue>{selected === "" ? DEFAULT_HALF_OPTION.name : nameOf(selected)}</SelectValue>
        </SelectTrigger>
        <SelectPopup align="end" alignItemWithTrigger={false}>
          {[DEFAULT_HALF_OPTION, ...options].map((option) => (
            <SelectItem hideIndicator key={option.id || "default"} value={option.id}>
              {option.name}
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    );
  };

  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
      {renderHalfSelect("light")}
      {renderHalfSelect("dark")}
    </div>
  );
}

function CustomThemeRow(props: {
  readonly onExport: () => void;
  readonly onRemove: () => void;
  readonly onSelect: () => void;
  readonly theme: CustomThemePalette;
}): ReactNode {
  return (
    <div className="flex items-center gap-2 rounded-[var(--control-radius)] px-3 py-2 text-xs hover:bg-accent/50">
      <button
        className="min-w-0 flex-1 truncate text-left font-medium text-foreground"
        onClick={props.onSelect}
        type="button"
      >
        {props.theme.name}
        <span className="ml-2 font-normal text-muted-foreground">{props.theme.id}</span>
      </button>
      <RowActionIconButton
        icon={<PencilIcon aria-hidden className="size-3.5" />}
        label={`Edit ${props.theme.name}`}
        onClick={() =>
          openThemeEditor({
            editingThemeId: props.theme.id,
            seedThemeId: null,
            seedName: null,
            initialAppearance: props.theme.appearance,
          })
        }
      />
      <RowActionIconButton
        icon={<DownloadIcon aria-hidden className="size-3.5" />}
        label={`Export ${props.theme.name}`}
        onClick={props.onExport}
      />
      <RowActionIconButton
        icon={<Trash2Icon aria-hidden className="size-3.5" />}
        label={`Remove ${props.theme.name}`}
        onClick={props.onRemove}
      />
    </div>
  );
}

/**
 * The custom-themes row: import on the control column, one management row per
 * installed theme underneath. Selection, removal cleanup, and the mix stay
 * with the panel through callbacks. Editing is the theme editor's session
 * store, which lives above the router; this section only opens sessions.
 */
export function ThemeCustomSection(props: {
  readonly resolvedTheme: ThemeAppearance;
  readonly themeId: string;
  readonly themeHalves: ThemeHalves | null;
  readonly onThemeIdChange: (id: string) => void;
  readonly onThemeRemoved: (id: string) => void;
}): ReactNode {
  const customThemes = useCustomThemes();

  const openNewThemeEditor = (): void => {
    // Seed the palette that is actually painted: a pinned half beats the base
    // preference, and the shipped default has nothing to copy.
    const paintedId = props.themeHalves?.[props.resolvedTheme] ?? props.themeId;
    openThemeEditor({
      editingThemeId: null,
      seedThemeId: paintedId === DEFAULT_THEME_ID ? null : paintedId,
      seedName: null,
      initialAppearance: props.resolvedTheme,
    });
  };

  return (
    <>
      <SettingsRow
        {...searchableSetting("appearance-custom-themes")}
        control={
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <button
              aria-label="New theme"
              className="inline-flex h-8 items-center gap-1.5 rounded-[var(--control-radius)] border border-border/70 px-3 text-xs font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={openNewThemeEditor}
              type="button"
            >
              <PlusIcon aria-hidden className="size-3.5" />
              New theme
            </button>
            <ImportThemeControl onImported={props.onThemeIdChange} />
          </div>
        }
        description="Theme files are JSON palettes; import one, or start the editor from what is painted now."
      />
      {customThemes.length > 0 ? (
        <div className="flex flex-col">
          {customThemes.map((theme) => (
            <CustomThemeRow
              key={theme.id}
              onExport={() => {
                downloadThemeFile(`${theme.id}.json`, serializeThemeFile(theme));
              }}
              onRemove={() => {
                removeCustomTheme(theme.id);
                props.onThemeRemoved(theme.id);
              }}
              onSelect={() => props.onThemeIdChange(theme.id)}
              theme={theme}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}
